import { Heading } from '@assembly-js/design-system';
import React from 'react';

import { ChevronDownIcon } from 'src/components/icons/ChevronDown';

interface AdvancedSettingsSectionProps {
  // Holds the panel open regardless of the toggle, so a blocking validation error can never sit
  // in a collapsed section with Save disabled and no visible field to point at.
  forceExpanded?: boolean;
  children: React.ReactNode;
}

// Collapses the app settings the app builder authors on the user's behalf, which otherwise crowd
// the page for the humans who almost never change them.
export function AdvancedSettingsSection({
  forceExpanded = false,
  children,
}: AdvancedSettingsSectionProps) {
  const id = React.useId();
  const titleId = `${id}-title`;
  const panelId = `${id}-panel`;
  const [isExpanded, setIsExpanded] = React.useState(forceExpanded);
  const isOpen = isExpanded || forceExpanded;

  // Latch the stored state open when validation forces the panel, so fixing the error doesn't
  // collapse the section out from under someone who is still editing it.
  React.useEffect(() => {
    if (forceExpanded) setIsExpanded(true);
  }, [forceExpanded]);

  return (
    <>
      <button
        type="button"
        id={titleId}
        aria-expanded={isOpen}
        aria-controls={panelId}
        // Validation owns the panel while it is forced open, so the toggle is inert rather than
        // silently recording a collapse that would fire later.
        aria-disabled={forceExpanded}
        onClick={() => {
          if (forceExpanded) return;
          setIsExpanded((prev) => !prev);
        }}
        className="flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-0 pt-6 pb-2 text-left"
      >
        <ChevronDownIcon
          className={`text-gray-small transition-transform duration-200 ${
            isOpen ? 'rotate-0' : '-rotate-90'
          }`}
          style={{ fontSize: 16 }}
        />
        <Heading size="lg" tag="span">
          Advanced
        </Heading>
      </button>
      {isOpen && (
        <div id={panelId} role="region" aria-labelledby={titleId}>
          {children}
        </div>
      )}
    </>
  );
}
